import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import api from '../../api';
import moment from 'moment';
import Button from '../button';
import ArticleContentEditor from '../article-content-editor';
import Checkbox from '../checkbox';
import Form from '../form';
import Input from '../input';
import ImageInput from '../image-input';
import DocumentInput from '../document-input';
import Loader from '../loader';
import './library-book-version-form.css';
import SummaryEditor from '../summary-editor/summary-editor';
import Select from '../select';
import TagInput from '../tag-input';

import hljs from 'highlight.js';
import json from 'highlight.js/lib/languages/json';
import 'highlight.js/styles/atom-one-dark.min.css';

hljs.registerLanguage('json', json);

const defaultBook = {
  ean: '',
  keywords: '',
  authorsOnlyNames: '',
  title: '',
  autoUpdateTitle: true,
  genre: '',
  autoUpdateGenre: true,
  coverId: null,
  autoUpdateCover: true,
  customLinkUrl: '',
  customLinkText: '',
  customLinkImageId: null,
  type: '',
  format: '',
  publisherName: '',
  collection: '',
  publishedAt: '',
  versions: [],
  language: '',
  nbPages: '',
  price: '',
  tagId: null,
  status: 'draft',
  publicationDate: '',
  includedInNewsletter: false,
  summary: '',
  autoUpdateSummary: true,
  googleContentAvailability: 'unknown',
  documentContentId: null,
  textContent: '',
  orbData: {},
};

const defaultErrors = {
  keywords: [],
  title: [],
  autoUpdateTitle: [],
  genre: [],
  autoUpdateGenre: [],
  coverId: [],
  autoUpdateCover: [],
  customLinkUrl: [],
  customLinkText: [],
  customLinkImageId: [],
  tagId: [],
  status: [],
  publicationDate: [],
  includedInNewsletter: [],
  summary: [],
  autoUpdateSummary: [],
  documentContentId: [],
  textContent: [],
};

const googleAvailabilityLabels = {
  unknown: 'Inconnu',
  available: 'Disponible',
  unavailable: 'Non disponible',
};

const googleAvailability = ({ googleContentAvailability }) => (
  <span className={`library-book-version-form_googleContentAvailability-badge library-book-version-form_googleContentAvailability-badge--${googleContentAvailability}`}>
    {googleAvailabilityLabels[googleContentAvailability]}
  </span>
);

const BookForm = props => {
  const [book, setBook] = useState(defaultBook);
  const [errors, setErrors] = useState(defaultErrors);
  const [isLoading, setIsLoading] = useState(props.id ? true : false);
  const [redirect, setRedirect] = useState('');
  const [showOrData, setShowOrData] = useState(false);

  const handleUpdateOrbData = e => {
    e.preventDefault();

    const apiMethod = api.libraryBookVersions.updateData;
    const apiParams = { id: book.id };

    e.preventDefault();
    setIsLoading(true);
    apiMethod(
      apiParams,
      (book) => {
        let newBook = {
          ...defaultBook,
          ...book,
          tagId: book.tag ? book.tag.id : null,
          eans: book.eans ? book.eans.split(',').map(k => k.trim()).filter(k => k.length > 0).join(', ') : null,
          coverId: book.cover ? book.cover.id : null,
          documentContentId: book.documentContent ? book.documentContent.id : null,
          customLinkImageId: book.customLinkImage ? book.customLinkImage.id : null,
          publicationDate: book.publicationDate ? moment(book.publicationDate).format('DD/MM/YYYY HH:mm') : '',
          publishedAt: book.publishedAt ? moment(book.publishedAt).format('DD/MM/YYYY') : '',
          price: book.price && parseFloat(book.price) ? parseFloat(book.price).toFixed(2) : '',
          keywords: book.keywords ? book.keywords.split(',').map(k => k.trim()).filter(k => k.length > 0).join(', ') : null,
        };

        delete newBook.legacyId;
        delete newBook.legacyUrl;
        delete newBook.legacyOrigin;
        delete newBook.shortUrl;
        delete newBook.slug;
        delete newBook.cover;
        delete newBook.customLinkImage;

        setBook(newBook);
        setIsLoading(false);
      },
      err => {
        if (err.response) {
          let newErrors = JSON.parse(JSON.stringify(defaultErrors));
          err.response.data.violations.map(error => {
            return newErrors[error.propertyPath].push(error.title);
          });
          setErrors(newErrors);
        } else {
          console.error(err);
        }
        setIsLoading(false);
      },
    );
  };

  const handleChange = e => {
    setBook({
      ...book,
      [e.target.name]: e.target.value,
    });
    setErrors({
      ...errors,
      [e.target.name]: [],
    });
  };

  const handleSubmit = e => {
    const apiMethod = props.id ? api.libraryBookVersions.put : api.libraryBookVersions.post;
    const apiParams = props.id ? { id: props.id, book } : { book };

    if (!book.textContent) {
      book.textContent = null;
    }

    if (!book.customLinkText) {
      book.customLinkText = null;
    }

    if (!book.customLinkUrl) {
      book.customLinkUrl = null;
    }

    book.publicationDate = book.publicationDate ? moment(book.publicationDate, 'DD/MM/YYYY HH:mm', true).format('YYYY-MM-DD HH:mm:ss') : '';

    e.preventDefault();
    setIsLoading(true);
    apiMethod(
      apiParams,
      () => {
        setRedirect('/librairie/livres');
      },
      err => {
        book.publicationDate = book.publicationDate ? moment(book.publicationDate, 'YYYY-MM-DD HH:mm:ss', true).format('DD/MM/YYYY HH:mm') : '';
        if (err.response) {
          let newErrors = JSON.parse(JSON.stringify(defaultErrors));
          err.response.data.violations.map(error => {
            return newErrors[error.propertyPath].push(error.title);
          });
          setErrors(newErrors);
        } else {
          console.error(err);
        }
        setIsLoading(false);
      },
    );
  };

  useEffect(() => {
    if (props.id) {
      setIsLoading(true);
      api.libraryBookVersions.get(
        props.id,
        book => {
          let newBook = {
            ...defaultBook,
            ...book,
            tagId: book.tag ? book.tag.id : null,
            coverId: book.cover ? book.cover.id : null,
            customLinkImageId: book.customLinkImage ? book.customLinkImage.id : null,
            documentContentId: book.documentContent ? book.documentContent.id : null,
            publicationDate: book.publicationDate ? moment(book.publicationDate).format('DD/MM/YYYY HH:mm') : '',
            publishedAt: book.publishedAt ? moment(book.publishedAt).format('DD/MM/YYYY') : '',
            price: book.price && parseFloat(book.price) ? parseFloat(book.price).toFixed(2) : '',
            keywords: book.keywords ? book.keywords.split(',').map(k => k.trim()).filter(k => k.length > 0).join(', ') : null,
          };

          delete newBook.legacyId;
          delete newBook.legacyUrl;
          delete newBook.legacyOrigin;
          delete newBook.shortUrl;
          delete newBook.slug;
          delete newBook.cover;
          delete newBook.customLinkImage;

          setBook(newBook);

          setIsLoading(false);
        },
      );
    } else {
      setBook(defaultBook);
    }
  }, [props.id]);

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="library-book-version-form">
      <Form
        className="library-book-version-form_form"
        onSubmit={handleSubmit}
      >
        <div className="library-book-version-form_column">
          <div className="library-book-version-form_infos">
            <p className="library-book-version-form_infos-title">Données ORB</p>
            <div style={{ display: 'flex', justifyContent: 'start', marginBottom: '10px' }}>
              <button onClick={handleUpdateOrbData} className="button button--info button--sm button--no-icon" type="button"><span className="button_label">Mettre à jour les données</span></button>
              <button style={{marginLeft: '5px'}} onClick={() => setShowOrData(!showOrData)} className="button button--secondary button--sm" type="button"><span className="button_label">Voir les données</span></button>
            </div>
            {showOrData && (
              <div className="library-book-version-form_infos-value" style={{ position: 'relative', height: '605px' }}>
                <pre>
                  <code dangerouslySetInnerHTML={{ __html: hljs.highlight(JSON.stringify(book.orbData, null, 2), { language: 'json' }).value }}></code>
                </pre>
              </div>
            )}
          </div>


          <div className="library-book-version-form_infos">
            <p className="library-book-version-form_infos-title">EAN</p>
            <p className="library-book-version-form_infos-value">
              {book.ean}
            </p>
            <p className="library-book-version-form_infos-title">Auteurs</p>
            <p className="library-book-version-form_infos-value">
              {book.authorsOnlyNames ? book.authorsOnlyNames : <i>Non renseigné</i>}
            </p>
          </div>

          <Input
            errors={errors.keywords}
            label="Mots-clés"
            tips="Séparés par une virgule"
            name="keywords"
            onChange={handleChange}
            type="text"
            value={book.keywords || ''}
          />

          <Input
            errors={errors.title}
            label="Titre"
            name="title"
            onChange={handleChange}
            style={{ width: '100%', marginRight: '10px' }}
            value={book.title}
            type="text"
          />

          <Checkbox
            errors={errors.autoUpdateTitle}
            label="Mettre à jour le titre automatiquement"
            name="autoUpdateTitle"
            onChange={handleChange}
            style={{ width: '100%', marginRight: '10px' }}
            value={book.autoUpdateTitle}
          />

          <Input
            errors={errors.genre}
            label="Genre"
            name="genre"
            onChange={handleChange}
            style={{ width: '100%', marginRight: '10px' }}
            value={book.genre ?? ''}
            type="text"
          />

          <Checkbox
            errors={errors.autoUpdateGenre}
            label="Mettre à jour le genre automatiquement"
            name="autoUpdateGenre"
            onChange={handleChange}
            style={{ width: '100%', marginRight: '10px' }}
            value={book.autoUpdateGenre}
          />

          <ImageInput
            errors={errors.coverId}
            label="Couverture"
            name="coverId"
            onChange={handleChange}
            value={book.coverId}
          />

          <Checkbox
            errors={errors.autoUpdateCover}
            label="Mettre à jour la couverture automatiquement"
            name="autoUpdateCover"
            onChange={handleChange}
            style={{ width: '100%', marginRight: '10px' }}
            value={book.autoUpdateCover}
          />

          <hr style={{ marginTop: '20px' }} />

          <div className="library-book-version-form_infos" style={{ marginTop: '0px' }}>
            <div className="library-book-version-form_infos-groups-container">
              <div style={{ marginRight: '10px' }}>
                <p className="library-book-version-form_infos-title">Type</p>
                <p className="library-book-version-form_infos-value">
                  {book.type ?? <i>Non renseigné</i>}
                </p>
              </div>
              <div style={{ marginLeft: '10px' }}>
                <p className="library-book-version-form_infos-title">Format</p>
                <p className="library-book-version-form_infos-value">
                  {book.format ?? <i>Non renseigné</i>}
                </p>
              </div>
            </div>
            <div className="library-book-version-form_infos-groups-container">
              <div style={{ marginRight: '10px' }}>
                <p className="library-book-version-form_infos-title">Editeur</p>
                <p className="library-book-version-form_infos-value">
                  {book.publisherName ?? <i>Non renseigné</i>}
                </p>
              </div>
              <div style={{ marginLeft: '10px' }}>
                <p className="library-book-version-form_infos-title">Collection</p>
                <p className="library-book-version-form_infos-value">
                  {book.collection ?? <i>Non renseigné</i>}
                </p>
              </div>
            </div>
            <div className="library-book-version-form_infos-groups-container">
              <div style={{ marginRight: '10px' }}>
                <p className="library-book-version-form_infos-title">Date de parution</p>
                <p className="library-book-version-form_infos-value">
                  {book.publishedAt ?? <i>Non renseigné</i>}
                </p>
              </div>
              <div style={{ marginLeft: '10px' }}>
                <p className="library-book-version-form_infos-title">Langue</p>
                <p className="library-book-version-form_infos-value">
                  {book.language ?? <i>Non renseigné</i>}
                </p>
              </div>
            </div>
            <div className="library-book-version-form_infos-groups-container">
              <div style={{ marginRight: '10px' }}>
                <p className="library-book-version-form_infos-title">Nb. pages</p>
                <p className="library-book-version-form_infos-value">
                  {book.nbPages ? `${book.nbPages} pages` : <i>Non renseigné</i>}
                </p>
              </div>
              <div style={{ marginLeft: '10px' }}>
                <p className="library-book-version-form_infos-title">Prix</p>
                <p className="library-book-version-form_infos-value">
                  {book.price ? `${book.price} €` : <i>Non renseigné</i>}
                </p>
              </div>
            </div>
          </div>

          <hr style={{ marginTop: '20px' }} />

          <div className="library-book-version-form_row">
            <TagInput
              errors={errors.tagId}
              label="Option"
              name="tagId"
              onChange={handleChange}
              style={{ width: '100%', marginRight: '10px' }}
              value={book.tagId}
            />

            <Select
              errors={errors.status}
              label="Statut"
              name="status"
              options={[
                {
                  label: 'En préparation',
                  value: 'draft',
                },
                {
                  label: 'Publié',
                  value: 'published',
                },
                {
                  label: 'Archivé',
                  value: 'archived',
                },
              ]}
              onChange={handleChange}
              style={{ width: '100%', marginLeft: '10px' }}
              value={book.status}
            />
          </div>

          <Input
            errors={errors.publicationDate}
            label="Date de publication"
            name="publicationDate"
            onChange={handleChange}
            style={{ width: '100%', marginRight: '10px' }}
            value={book.publicationDate}
            tips="JJ/MM/AAAA HH:mm"
            type="text"
          />

          <Checkbox
            errors={errors.includedInNewsletter}
            label="Newsletter"
            name="includedInNewsletter"
            onChange={handleChange}
            style={{ width: '100%', marginRight: '10px' }}
            value={book.includedInNewsletter}
          />
        </div>

        <div className="library-book-version-form_column">
          <Input
            errors={errors.customLinkUrl}
            label="Lien personalisé"
            name="customLinkUrl"
            onChange={handleChange}
            type="text"
            value={book.customLinkUrl || ''}
          />

          <Input
            errors={errors.customLinkText}
            label="Texte du lien personalisé"
            name="customLinkText"
            onChange={handleChange}
            type="text"
            value={book.customLinkText || ''}
          />

          <div className="library-book-version-form_custom-link-image">
            <ImageInput
              errors={errors.customLinkImageId}
              label="Image du lien personalisé"
              name="customLinkImageId"
              onChange={handleChange}
              value={book.customLinkImageId}
            />
          </div>

          <SummaryEditor
            errors={errors.summary}
            label="Résumé"
            name="summary"
            onChange={handleChange}
            value={book.summary}
          />

          <Checkbox
            errors={errors.autoUpdateSummary}
            label="Mettre à jour le résumé automatiquement"
            name="autoUpdateSummary"
            onChange={handleChange}
            style={{ width: '100%', marginRight: '10px' }}
            value={book.autoUpdateSummary}
          />

          <div className="library-book-version-form_infos">
            <p className="library-book-version-form_infos-title">Statut Google Book</p>
            <p className="library-book-version-form_infos-value">
              {googleAvailability(book)}
            </p>
          </div>

          <DocumentInput
            errors={errors.documentContentId}
            label="Extrait : document"
            name="documentContentId"
            onChange={handleChange}
            value={book.documentContentId}
          />

          <ArticleContentEditor
            errors={errors.textContent}
            label="Extrait : texte"
            name="textContent"
            onChange={handleChange}
            value={book.textContent}
          />
        </div>

        <Button
          className="library-book-version-form_submit-button"
          label={`${props.id ? 'Modifier' : 'Créer'} le livre`}
          theme="success"
          type="submit"
        />

      </Form>
    </div>
  );
};

BookForm.propTypes = {
  id: PropTypes.number,
};

export default BookForm;
